import type { StageDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { DIAGNOSTIC_PASSTHROUGH } from "../recipes";
import { pollutionCardsFor } from "../pollution";

/**
 * Schema/camera boundary fixture (spec 0017 T4, acceptance criterion 8) — NOT
 * curriculum, and not meant to be. `stageSchema` caps a board at 16 cols x 10
 * rows; this is a real, loadable stage sized exactly at that maximum, so a
 * camera-framing check (a `packages/game` task, not this one) has something
 * that large to actually run against. Loadable from the job sheet only, like
 * Tyrian Purple: it never enters a run (see FREE_PLAY_STAGES in
 * packages/content/src/index.ts).
 *
 * Kept as simple as the schema allows: one feed at the far left, one hopper
 * at the far right, one pipe the whole width of the board. The feed reaches
 * the hopper unattended, so the worked solution is "do nothing."
 * `DIAGNOSTIC_PASSTHROUGH` exists only to satisfy `stageSchema`'s "at least
 * one recipe" rule — nothing on this board ever pools a buffer against it.
 */
export const STAGE_DIAGNOSTIC_MAX_BOARD: StageDef = {
  id: "stage-diagnostic-16x10-board",
  name: "Diagnostic: Max Board",
  blurb: "A schema boundary fixture, not a lesson: the largest board the engine allows.",
  cols: 16,
  rows: 10,
  nodes: [
    { id: "in-feed", kind: "inputTank", pos: { x: 0, y: 5 }, emits: { DiagnosticFeed: 1 } },
    { id: "out-collect", kind: "productHopper", pos: { x: 15, y: 5 }, bufferCapacity: 99 },
  ],
  edges: [
    {
      id: "e-through",
      fromNodeId: "in-feed",
      toNodeId: "out-collect",
      material: "glass",
      // The full 16-tile width: one waypoint per tile, per `edgeSchema`'s
      // orthogonal-contiguity refinement.
      path: Array.from({ length: 16 }, (_, x) => ({ x, y: 5 })),
    },
  ],
  recipes: [DIAGNOSTIC_PASSTHROUGH],
  objective: {
    targetComposition: { DiagnosticFeed: 3 },
    minPurity: 100,
  },
  energyBudgetKw: 6,
  handSize: 5,
  // Generous: a packet takes 15 ticks to cross the 16-tile pipe plus one to
  // be pushed off the tank, so quota is not reachable in fewer than ~19.
  ticksPerTurn: 30,
  maxTurns: 1,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances(["card-pipe-glass"]),
  // Spec 0029: its one edge is a glass pipe, so reachablePollutionReasons
  // says burst, spill and leak, same as any real board with a glass line.
  pollutionCards: pollutionCardsFor("burst", "spill", "leak"),
};
